import { css } from 'styled-components';

/**
 * Adds styles at the given token breakpoint.
 *
 * @example
 * import { css } from 'styled-components';
 * import { mediaMixin } from '@zillow/constellation';
 *
 * const styles = css`
 *     color: red;
 * `;
 *
 * const Component = styled.div`
 *     ${mediaMixin('md', styles)}
 * `;
 *
 * <Component>This text will be red at the "md" breakpoint</Component>
 *
 * @method
 * @param {string} breakpoint - A breakpoint media token. By default, the styles will apply to sizes
 *     greater than or equal (gte) to the the breakpoint value. You can also apply styles at sizes
 *     less than or equal (lte), or exactly equal by appending to the breakpoint,
 *     e.g. sm_lte, md_eq, lg_gte.
 * @param {CSSObject} styles - The styles that should be applied at the given breakpoint.
 * @return {Interpolation}
 */
export const mediaMixin = (breakpoint, styles) => (props) => {
  // DEPRECATED: Please use mediaMixin from constellatoin
  // eslint-disable-next-line no-console
  console.warn(
    `The 'media' property from CometTheme will be removed in the next MAJOR release, use 'mediaMixin' from constellation instead.
        http://constellation.pages.zgtools.net/constellation/master/jsdocs/global.html#mediaMixin`,
  );

  const split = breakpoint.split('_');
  const shirtSize = split[0];
  const strategy = split[1] || 'gte';

  const breakpoints = props.theme.comet.media.BREAKPOINTS;
  if (!breakpoints) {
    return null;
  }

  const keys = Object.keys(breakpoints);
  const value = breakpoints[shirtSize];
  const index = keys.indexOf(shirtSize);

  if (index < 0) {
    throw new Error(
      `The breakpoint size '${shirtSize}' does not exist on the theme. Use one of ${keys
        .map((k) => `'${k}'`)
        .join(', ')}.`,
    );
  }

  if (['lte', 'eq', 'gte'].indexOf(strategy) < 0) {
    throw new Error(
      `The strategy '${strategy}' is not valid for breakpoint '${breakpoint}'. Use one of '${shirtSize}_lte', '${shirtSize}_eq', '${shirtSize}_gte'.`,
    );
  }

  let min;
  if (strategy !== 'lte') {
    min = `(min-width: ${value}px)`;
  }

  let max;
  if (strategy !== 'gte' && index < keys.length - 1) {
    max = `(max-width: ${breakpoints[keys[index + 1]] - 1}px)`;
  }

  if (min || max) {
    return css`
      @media ${min}${min && max ? ' and ' : ''}${max} {
        ${typeof styles === 'function' ? styles(props) : styles}
      }
    `;
  }

  // If there is no min or max, insert the styles as-is
  return css`
    ${typeof styles === 'function' ? styles(props) : styles}
  `;
};
